import type { LoyaltyTotals } from '~/hooks/apis/reporting/useLoyaltyAnalytics';

import { RATIO_MEASURES, STOCK_MEASURES, sumKeys } from './loyaltyMeasures';

type Point = { hasData: boolean } & LoyaltyTotals;
type Read = (point: Point) => number | null;

const field =
  (key: keyof LoyaltyTotals): Read =>
  (point) =>
    point[key] ?? null;

/** Ratio of the bucket's own sums, never an average of ratios. Null when the denominator is empty. */
const rate =
  (id: string): Read =>
  (point) => {
    const numerator = sumKeys(point, RATIO_MEASURES[id].numerator);
    const denominator = sumKeys(point, RATIO_MEASURES[id].denominator);
    return numerator == null || denominator == null || denominator === 0 ? null : numerator / denominator;
  };

const READERS: Record<string, Read> = {
  ...Object.fromEntries(Object.keys(RATIO_MEASURES).map((id) => [id, rate(id)])),
  'loyalty.points_earned': field('earned'),
  'loyalty.points_burned': field('burned'),
  'loyalty.points_redeemed': field('redeemed'),
  'loyalty.points_sales_aud': field('pointsSales'),
  'loyalty.points_sales_gross_aud': field('grossSales'),
  'loyalty.periods_ended': field('ended'),
  'loyalty.periods_renewed': field('renewed'),
  'loyalty.periods_renewed_then_cancelled': field('cancelled'),
  'loyalty.periods_lapsed': field('lapsed'),
  'loyalty.periods_awaiting': field('awaiting'),
  'loyalty.luxplus_savings_aud': field('savings'),
  'loyalty.membership_margin_ar': field('margin'),
  'loyalty.membership_orders': field('orders'),
  'loyalty.luxplus_new_members': field('newMembers'),
  ...Object.fromEntries(Object.entries(STOCK_MEASURES).map(([id, measure]) => [id, field(measure.stock)])),
};

/** Member counts and joiners are read on every day: their days do not depend on points or renewal rows. */
const MEMBER_METRICS: ReadonlySet<string> = new Set([...Object.keys(STOCK_MEASURES), 'loyalty.luxplus_new_members']);

/** One value per trend bucket for each metric the API returns components for; days without data are null. */
export function loyaltySeries(
  metricIds: ReadonlyArray<string>,
  trend: ReadonlyArray<Point>,
): Record<string, Array<number | null>> {
  return Object.fromEntries(
    metricIds
      .filter((id) => READERS[id])
      .map((id) => [id, trend.map((point) => (point.hasData || MEMBER_METRICS.has(id) ? READERS[id](point) : null))]),
  );
}
